import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type CSSProperties } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
// .cty-flag-lg / .cty-flag-acts / .cty-flag-btn — the shared XAF ImageEditor-parity
// preview + edit/erase buttons reused by the Contact popup's Photo field.
import '../accounting/accounting.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { DEFAULT_DATE_RANGE } from '../../components/listDefaults';
import { DateRangeSelect, TRACKING_RANGES } from '../modules/DateRangeSelect';
import { fmtLocalDate, fmtLocalDateTime } from '../../utils/datetime';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { runLoading, BusyIcon } from '../../components/loading';
import { useNavActions } from '../../hooks/useNavActions';
import { ClientDetailModal } from './ClientDetailModal';
import { CountryDetailModal } from './CountryDetailModal';
import { StateDetailModal } from './StateDetailModal';
import { CityDetailModal } from './CityDetailModal';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface SampleReceiptRow {
  jobId:        string;
  projectId:    string;
  projectName:  string;
  client:       string;
  receivedDate: string | null;
  tatDays:      number | null;
  dueDate:      string | null;
  email:        string;
  sendBy:       string;
  method:       string;
  status:       string;
  mailStatus:   number | null;
  sentDate:     string | null;
  updatedDate:  string | null;
}

type StatusFilter = 'All' | 'Sent' | 'InQueue' | 'Failed';
type DateRangeKey = '1M' | '3M' | '6M' | '1Y' | '2Y' | '5Y' | 'All';

// "New Contact" form — full GoLive Contact-dialog field set.
interface NewContact {
  firstName: string; middleName: string; lastName: string;
  gender: string;            // '' / '0' None / '1' Male / '2' Female
  birthday: string;          // free text (empty by default — GoLive parity)
  clientId: string; clientName: string;   // chosen client (Client picker); default = job's client
  email: string; emailCc: string;
  officePhone: string; homePhone: string; mobilePhone: string; otherPhone: string; fax: string;
  street1: string; street2: string;
  countryId: string; stateId: string; cityId: string;
  zip: string; position: string; account: string;
  prospect: boolean;
  photoName: string; photoBase64: string;
  sampleReceiptNotification: boolean; reportDelivery: boolean;
}
type NcTextKey =
  | 'firstName' | 'middleName' | 'lastName' | 'email' | 'emailCc'
  | 'officePhone' | 'homePhone' | 'mobilePhone' | 'otherPhone' | 'fax'
  | 'street1' | 'street2' | 'zip' | 'position' | 'account';
const EMPTY_CONTACT: NewContact = {
  firstName: '', middleName: '', lastName: '', gender: '', birthday: '',
  clientId: '', clientName: '',
  email: '', emailCc: '',
  officePhone: '', homePhone: '', mobilePhone: '', otherPhone: '', fax: '',
  street1: '', street2: '', countryId: '', stateId: '', cityId: '',
  zip: '', position: '', account: '', prospect: false,
  photoName: '', photoBase64: '',
  sampleReceiptNotification: true, reportDelivery: false,
};
interface GeoOpt { id: string; name: string; parentId: string | null; }

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
// Render via the shared §14b.6 helpers (local tz, 24-hour, no AM/PM); keep the
// '—' placeholder for empty/unparseable values used in grid cells + CSV export.
function fmtDateTime(v?: string | null): string {
  return v ? (fmtLocalDateTime(v) || '—') : '—';
}

function fmtDate(v?: string | null): string {
  return v ? (fmtLocalDate(v) || '—') : '—';
}

function fmtTat(n: number | null): string {
  if (n === null || n === undefined) return '—';
  return `${n} Day${n === 1 ? '' : 's'}`;
}

function cutoffDate(key: DateRangeKey): Date | null {
  if (key === 'All') return null;
  const now = new Date();
  const map: Record<Exclude<DateRangeKey, 'All'>, () => Date> = {
    '1M': () => new Date(now.getFullYear(), now.getMonth() - 1,  now.getDate()),
    '3M': () => new Date(now.getFullYear(), now.getMonth() - 3,  now.getDate()),
    '6M': () => new Date(now.getFullYear(), now.getMonth() - 6,  now.getDate()),
    '1Y': () => new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()),
    '2Y': () => new Date(now.getFullYear() - 2, now.getMonth(), now.getDate()),
    '5Y': () => new Date(now.getFullYear() - 5, now.getMonth(), now.getDate()),
  };
  return map[key as Exclude<DateRangeKey, 'All'>]();
}

// Status → shared pill variant (backlog.css .bl-pill ok/warn/alert/info) —
// the page's original severity semantics, rendered through the shared pill
// classes so the Status column is the ONLY coloured cell (BO UI Standard).
function statusPillVariant(status: string): string {
  if (status === 'Sent')    return 'ok';     // green
  if (status === 'InQueue') return 'warn';   // amber
  if (status === 'Failed')  return 'alert';  // red
  return 'info';
}


// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function SampleReceiptNotificationPage() {
  const [rows,       setRows]       = useState<SampleReceiptRow[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [loadMs,     setLoadMs]     = useState<number | null>(null);
  const [error,      setError]      = useState<string | null>(null);
  const [dateRange,  setDateRange]  = useState<DateRangeKey>(DEFAULT_DATE_RANGE.SampleTracking as DateRangeKey);
  const [statusFilt, setStatusFilt] = useState<StatusFilter>('All');
  const [search,     setSearch]     = useState('');
  const [selected,   setSelected]   = useState<Set<string | number>>(new Set());
  const [sending,    setSending]    = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  // Reports ▾ items — GoLive Custom Report Builder / Form Report Permissions
  // (templates assigned to the "Sample Receipt Notification" form). Dynamic, not
  // hardcoded: assigning COC to the form in Custom Report Builder makes it appear.
  const [reportItems, setReportItems] =
    useState<{ id: number; label: string; templateName: string; allowMultipleJobId: boolean }[]>([]);
  // The grid's live filtered+sorted rows (per-column filters applied inside
  // AppDataGrid) — Export uses THIS so it exports exactly what's on screen. null
  // until the grid reports once → export falls back to `filtered` on first paint.
  const [gridRows,   setGridRows]   = useState<SampleReceiptRow[] | null>(null);
  const searchRef  = useRef<HTMLInputElement>(null);
  const notify  = useNotify();
  const { confirm } = useConfirm();
  // Module Access Control (Role-Based MAC) action gating for this form, GoLive
  // parity: Read gates the data, Write gates the mutating actions (Send + the
  // recipient/email edit + New Contact). Falls open (all true) for admins /
  // before permissions load / when no MAC level is configured — so it only ever
  // TIGHTENS access where the lab set a MAC, never removes an existing action.
  const perm = useNavActions('sampreceipt');

  // Contact Email popup (double-click the Email cell — GoLive parity).
  const [pickerRow,     setPickerRow]     = useState<SampleReceiptRow | null>(null);
  const [contactEmails, setContactEmails] = useState<{ oid: string; email: string; name: string }[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  // Selected CONTACT Oids (multi-select, GoLive Contact_LookupListView parity).
  // Keyed by Oid — NOT email — so ticking one row never ticks other rows that
  // happen to share the same email address (the client can hold the same email
  // on several contacts).
  const [pickerSel,     setPickerSel]     = useState<string[]>([]);
  const [pickerSearch,  setPickerSearch]  = useState('');
  // Lookup-grid pager (GoLive Contact_LookupListView footer: count · pages · page size).
  const [pickerPage,     setPickerPage]     = useState(0);
  const [pickerPageSize, setPickerPageSize] = useState(20);
  // Maximize/restore — GoLive puts the maximize AND close pair on EVERY popup.
  const [pickerMax,      setPickerMax]      = useState(false);
  // New Contact form (the popup's "New" button — GoLive Contact dialog).
  const [ncOpen,   setNcOpen]   = useState(false);
  const [ncSaving, setNcSaving] = useState(false);
  const [nc,       setNc]       = useState<NewContact>(EMPTY_CONTACT);
  const [ncMax,    setNcMax]    = useState(false);   // maximize the New Contact popup
  // The Photo field's hidden <input type="file"> — driven by the Browse… button
  // when empty and by the pencil once an image is chosen (GoLive ImageEditor).
  const ncPhotoRef = useRef<HTMLInputElement>(null);
  // Cascading Country → State → City options (reuse existing lookup endpoints).
  const [geoCountries, setGeoCountries] = useState<GeoOpt[]>([]);
  const [geoStates,    setGeoStates]    = useState<GeoOpt[]>([]);
  const [geoCities,    setGeoCities]    = useState<GeoOpt[]>([]);
  // Client picker (GoLive Client lookup popup).
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [clients,          setClients]          = useState<{ oid: string; name: string; address: string; phone: string }[]>([]);
  const [clientSearch,     setClientSearch]     = useState('');
  const [clientSort,       setClientSort]       = useState<{ key: 'name' | 'address' | 'phone'; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' });
  const [clientSel,        setClientSel]        = useState('');
  const [clientPage,       setClientPage]       = useState(0);
  // Per-column Excel-style filter (GoLive header dropdown): checked values per
  // column; empty = all. openColFilter = which column's popup is open.
  const [colFilter,        setColFilter]        = useState<{ name: string[]; address: string[]; phone: string[] }>({ name: [], address: [], phone: [] });
  const [openColFilter,    setOpenColFilter]    = useState<'name' | 'address' | 'phone' | null>(null);
  const [colFilterSearch,  setColFilterSearch]  = useState('');
  const [clientPageSize,   setClientPageSize]   = useState(20);
  const [clientPickerMax,  setClientPickerMax]  = useState(false);   // GoLive popup chrome
  // Client Details (Edit icon in the Client picker — GoLive "{Client} - Clients" popup).
  const [clientDetailOpen, setClientDetailOpen] = useState(false);
  const [clientDetailId,   setClientDetailId]   = useState('');
  // Country / State / City create-edit popups (GoLive: EVERY geo lookup carries a
  // New-Edit (✎) button next to its dropdown, not just Country).
  // 'new' opens a blank create form, 'edit' opens the selected record (GoLive
  // LookupPropertyEditor New vs Edit — New must NOT pre-fill an already-filled value).
  const [countryModalOpen, setCountryModalOpen] = useState<false | 'new' | 'edit'>(false);
  const [stateModalOpen,   setStateModalOpen]   = useState<false | 'new' | 'edit'>(false);
  const [cityModalOpen,    setCityModalOpen]    = useState<false | 'new' | 'edit'>(false);

  const loadClients = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/sample-receipt-notification/clients');
      setClients((res.data as { data?: { oid: string; name: string; address: string; phone: string }[] }).data ?? []);
    } catch { /* leave empty on failure */ }
  }, []);

  // Load the client list once, when the Client picker first opens.
  useEffect(() => {
    if (!clientPickerOpen || clients.length) return;
    void loadClients();
  }, [clientPickerOpen, clients.length, loadClients]);

  const openClientDetail = useCallback(async (oid: string) => {
    setClientDetailId(oid);
    setClientDetailOpen(true);
  }, []);

  const sortedFilteredClients = useMemo(() => {
    const t = clientSearch.trim().toLowerCase();
    let r = t ? clients.filter(c => `${c.name} ${c.address} ${c.phone}`.toLowerCase().includes(t)) : clients;
    // Per-column checklist filters (a column with picks keeps only those values).
    (['name', 'address', 'phone'] as const).forEach(k => {
      if (colFilter[k].length) r = r.filter(c => colFilter[k].includes(c[k] ?? ''));
    });
    r = [...r].sort((a, b) => {
      const av = a[clientSort.key] ?? '', bv = b[clientSort.key] ?? '';
      const cmp = av.localeCompare(bv);
      return clientSort.dir === 'asc' ? cmp : -cmp;
    });
    return r;
  }, [clients, clientSearch, clientSort, colFilter]);

  // Distinct values for a column's filter popup (from the text-search-filtered set,
  // excluding that column's own picks so you can widen it again — GoLive parity).
  const colFilterValues = useMemo(() => {
    if (!openColFilter) return [] as string[];
    const t = clientSearch.trim().toLowerCase();
    const base = t ? clients.filter(c => `${c.name} ${c.address} ${c.phone}`.toLowerCase().includes(t)) : clients;
    const withOthers = base.filter(c =>
      (['name', 'address', 'phone'] as const).every(k =>
        k === openColFilter || !colFilter[k].length || colFilter[k].includes(c[k] ?? '')));
    const s = colFilterSearch.trim().toLowerCase();
    return Array.from(new Set(withOthers.map(c => c[openColFilter] ?? '')))
      .filter(v => !s || v.toLowerCase().includes(s))
      .sort((a, b) => a.localeCompare(b));
  }, [openColFilter, clients, clientSearch, colFilter, colFilterSearch]);

  const clientTotalPages = Math.max(1, Math.ceil(sortedFilteredClients.length / clientPageSize));
  const pagedClients = useMemo(() =>
    sortedFilteredClients.slice(clientPage * clientPageSize, (clientPage + 1) * clientPageSize),
    [sortedFilteredClients, clientPage, clientPageSize]);

  // Load the Country/State/City lookups — callable so the Country popup can
  // refresh them after a new country is created (GoLive Country edit icon).
  const loadGeo = useCallback(async () => {
    try {
      const [cs, st, ci] = await Promise.all([
        api.get('/api/v1/system/states/countries'),
        api.get('/api/v1/cities/states'),
        api.get('/api/v1/cities'),
      ]);
      setGeoCountries(((cs.data as { data?: { id: string; name: string }[] }).data ?? [])
        .map(c => ({ id: c.id, name: c.name, parentId: null })));
      setGeoStates(((st.data as { data?: { id: string; shortName: string; longName: string | null; countryId: string | null }[] }).data ?? [])
        .map(s => ({ id: s.id, name: s.longName || s.shortName, parentId: s.countryId })));
      setGeoCities(((ci.data as { data?: { id: string; cityName: string | null; stateId: string | null }[] }).data ?? [])
        .map(c => ({ id: c.id, name: c.cityName ?? '', parentId: c.stateId })));
    } catch { /* geo dropdowns are optional — leave empty on failure */ }
  }, []);

  // Refresh the geo lookups EVERY time the New Contact form opens — not just the
  // first time — so a Country/State/City added elsewhere (e.g. the System
  // Management screens, or another app on the shared DB) shows up in the dropdowns
  // without a full page reload. The old `geoCountries.length` guard cached the very
  // first fetch forever, so a later-added country never appeared.
  useEffect(() => {
    if (!ncOpen) return;
    void loadGeo();
  }, [ncOpen, loadGeo]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    // Read-gate (MAC, GoLive parity): if the user's MAC level has no Read on this
    // form, show nothing and don't fetch. Falls open (perm.read=true) for admins,
    // before permissions load, or when no MAC is configured.
    if (!perm.read) { setRows([]); setError(null); setLoading(false); return; }
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      const params: Record<string, string> = {};
      const co = cutoffDate(dateRange);
      if (co) {
        const pad = (n: number) => String(n).padStart(2, '0');
        params.startDate = `${co.getFullYear()}-${pad(co.getMonth() + 1)}-${pad(co.getDate())}`;
        params.endDate   = `${new Date().getFullYear()}-${pad(new Date().getMonth() + 1)}-${pad(new Date().getDate())}`;
      }
      const res = await api.get('/api/v1/sample-receipt-notification', { params });
      setRows((res.data as { data: SampleReceiptRow[] }).data ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [dateRange, perm.read]);

  useEffect(() => { void load(); }, [load]);

  // Load the Reports ▾ menu once (form→template assignments, GoLive parity).
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/v1/sample-receipt-notification/reports');
        setReportItems((res.data as { data?: typeof reportItems }).data ?? []);
      } catch { /* leave empty → the dropdown falls back to the built-in Sample Receipt */ }
    })();
  }, []);

  // The ticked JobIDs (both actions operate on the selection, GoLive parity).
  const selectedJobIds = useMemo(() => Array.from(selected).map(String), [selected]);

  // ── Reports ▾ → Sample Receipt — open the receipt PDF for the ticked job(s)
  // in a new tab (GoLive SampleReceipt_Execute: one combined PDF, one job/page). ─
  const openReceipt = useCallback(async () => {
    setReportsOpen(false);
    if (selectedJobIds.length === 0) {
      notify.info('Select at least one row to preview its Sample Receipt.');
      return;
    }
    // GoLive parity: open the report as a BLOB in the browser's NATIVE PDF viewer
    // (GoLive's tab is a blob:… URL — screenshot). A real ".pdf" file URL gets
    // hijacked by the Adobe Acrobat browser extension (its cluttered viewer); a
    // blob URL is NOT intercepted, so it renders cleanly in-browser like GoLive.
    // Same mechanism as Custom Reporting's render-devx preview.
    try {
      const res = await runLoading(
        api.get('/api/v1/sample-receipt-notification/receipt-pdf', {
          params: { jobIds: selectedJobIds.join(',') }, responseType: 'blob',
        }),
        { blocking: true, label: 'Generating Sample Receipt…' });
      const url = URL.createObjectURL(res.data as Blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      notify.error('Failed to generate the Sample Receipt.');
    }
  }, [selectedJobIds, notify]);

  // ── Reports ▾ → COC — open the Chain-of-Custody PDF for the ticked job (GoLive
  // COC report). COC is a single-job report (AllowMultipleJOBID=false), so it uses
  // the first selected job; 204 means the job has no COC upload. ─────────────────
  const openCoc = useCallback(async () => {
    setReportsOpen(false);
    if (selectedJobIds.length === 0) {
      notify.info('Select a row to preview its COC.');
      return;
    }
    const jobId = selectedJobIds[0];
    try {
      const res = await runLoading(
        api.get('/api/v1/sample-receipt-notification/coc-pdf', {
          params: { jobId }, responseType: 'blob',
        }),
        { blocking: true, label: 'Generating COC…' });
      const blob = res.data as Blob;
      if (res.status === 204 || !blob || blob.size === 0) {
        notify.info(`No COC attachment for job ${jobId}.`);
        return;
      }
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      notify.error('Failed to generate the COC.');
    }
  }, [selectedJobIds, notify]);

  // Route a Reports ▾ item to the right generator by its template name / label
  // (GoLive maps each Custom Report Builder template to its report). This form's
  // two templates: "COC" → the COC PDF, "xrReceiptNotification"/"Sample Receipt"
  // → the Sample Receipt PDF.
  const runReport = useCallback((it: { templateName: string; label: string }) => {
    const key = `${it.templateName} ${it.label}`.toLowerCase();
    if (key.includes('coc')) void openCoc();
    else void openReceipt();
  }, [openCoc, openReceipt]);

  // ── Send — email the sample receipt for the ticked job(s) and stamp them Sent
  // (GoLive SentMail_Execute → Sendmail). Confirm → POST → toast → reload. ───────
  const sendSelected = useCallback(async () => {
    if (selectedJobIds.length === 0) {
      notify.info('Select at least one row to send.');
      return;
    }
    const n = selectedJobIds.length;
    const ok = await confirm({
      title: 'Send',
      message: `Send the sample receipt notification for ${n} job${n === 1 ? '' : 's'}?`,
    });
    if (!ok) return;

    setSending(true);
    try {
      const res = await runLoading(
        api.post('/api/v1/sample-receipt-notification/send', { jobIds: selectedJobIds.join(',') }),
        { blocking: true, label: 'Sending…' });
      const d = (res.data as { data?: { sent: number; skipped: number; failed: number } }).data
        ?? { sent: 0, skipped: 0, failed: 0 };
      if (d.failed > 0)
        notify.error(`${d.failed} failed to send${d.sent ? `, ${d.sent} sent` : ''}${d.skipped ? `, ${d.skipped} skipped` : ''}.`);
      else if (d.skipped > 0)
        notify.warning(`${d.sent} sent, ${d.skipped} skipped — no recipient or SMTP sender configured.`);
      else
        notify.success(`Sent ${d.sent} sample receipt notification${d.sent === 1 ? '' : 's'}.`);
      setSelected(new Set());
      await load();
    } catch {
      notify.error('Failed to send the sample receipt notification.');
    } finally {
      setSending(false);
    }
  }, [selectedJobIds, confirm, notify, load]);

  // ── Contact Email popup ──────────────────────────────────────────────────
  const openEmailPicker = useCallback(async (row: SampleReceiptRow) => {
    // GoLive opens the picker with NOTHING ticked — even when the row already has
    // an email — so clicking OK without ticking a checkbox raises the validation
    // "Select email id to continue". Pre-seeding pickerSel with row.email defeated
    // that (OK saw a non-empty value and skipped the check), so start empty.
    setPickerRow(row); setPickerSel([]); setPickerSearch(''); setContactEmails([]); setPickerPage(0); setPickerMax(false);
    setPickerLoading(true);
    try {
      const res = await api.get(`/api/v1/sample-receipt-notification/${encodeURIComponent(row.jobId)}/contact-emails`);
      setContactEmails((res.data as { data: { oid: string; email: string; name: string }[] }).data ?? []);
    } catch {
      notify.error('Failed to load contact emails.');
    } finally {
      setPickerLoading(false);
    }
  }, [notify]);

  const applyEmail = useCallback(async (email: string) => {
    if (!pickerRow) return;
    const jobId = pickerRow.jobId;
    const trimmed = email.trim();
    // GoLive keeps OK enabled with nothing ticked and answers with the error
    // "Select email id to continue" (verified against the local GoLive instance).
    // AI had OK DISABLED, so the user got no feedback at all. Same wording here;
    // it surfaces through the shared notifier, which routes errors to the centred
    // modal per the two-tier message standard (§18.6) rather than GoLive's banner.
    if (!trimmed) { notify.error('Select email id to continue'); return; }
    try {
      await runLoading(
        api.put(`/api/v1/sample-receipt-notification/${encodeURIComponent(jobId)}/email`, { email: trimmed }),
        { blocking: true, label: 'Saving…' });
      setRows(rs => rs.map(r => r.jobId === jobId ? { ...r, email: trimmed } : r));
      notify.success('Recipient email updated.');
      setPickerRow(null);
    } catch {
      notify.error('Failed to update the recipient email.');
    }
  }, [pickerRow, notify]);

  // Create the Contact (GoLive "New"). GoLive parity: "New" ONLY adds the contact
  // to the CLIENT (dbo.Contact) — it does NOT set the job's recipient email / touch
  // the grid's Email column. After saving we return to the Contact Email lookup and
  // refresh its list so the new contact appears as a selectable row; the user then
  // ticks it + OK to actually set the recipient (or Cancel to leave the email as-is).
  const saveNewContact = useCallback(async () => {
    if (!pickerRow) return;
    if (!nc.firstName.trim() || !nc.email.trim()) {
      notify.warning('First Name and Email are required.');
      return;
    }
    setNcSaving(true);
    try {
      const body = {
        firstName: nc.firstName, middleName: nc.middleName, lastName: nc.lastName,
        gender: nc.gender ? Number(nc.gender) : null,
        birthday: nc.birthday || null,
        clientId: nc.clientId || null,
        email: nc.email, emailCc: nc.emailCc,
        officePhone: nc.officePhone, homePhone: nc.homePhone, mobilePhone: nc.mobilePhone,
        otherPhone: nc.otherPhone, fax: nc.fax,
        street1: nc.street1, street2: nc.street2,
        countryId: nc.countryId || null, stateId: nc.stateId || null, cityId: nc.cityId || null,
        zip: nc.zip, position: nc.position, account: nc.account,
        prospect: nc.prospect,
        photoBase64: nc.photoBase64 || null,
        sampleReceiptNotification: nc.sampleReceiptNotification, reportDelivery: nc.reportDelivery,
      };
      await runLoading(
        api.post(`/api/v1/sample-receipt-notification/${encodeURIComponent(pickerRow.jobId)}/contact`, body),
        { blocking: true, label: 'Creating contact…' });
      setNcOpen(false);
      notify.success('Contact created.');
      // Refresh the lookup list so the just-created contact shows (only when its
      // Sample Receipt Notification flag is on — the popup lists SRN contacts).
      try {
        const r = await api.get(`/api/v1/sample-receipt-notification/${encodeURIComponent(pickerRow.jobId)}/contact-emails`);
        setContactEmails((r.data as { data: { oid: string; email: string; name: string }[] }).data ?? []);
      } catch { /* keep the current list */ }
    } catch {
      notify.error('Failed to create the contact.');
    } finally {
      setNcSaving(false);
    }
  }, [pickerRow, nc, notify]);

  // ── New Contact form field helpers (GoLive Contact-dialog 2-column layout) ──
  const ncInput = (highlight: boolean): CSSProperties => ({
    width: '100%', padding: '4px 7px', border: '1px solid #cbd5e1', borderRadius: 5,
    boxSizing: 'border-box', fontSize: 12, background: highlight ? '#fffbe7' : '#fff',
  });
  const ncRow = (label: string, control: ReactNode, req = false) => (
    <div style={{ display: 'grid', gridTemplateColumns: '94px 1fr', alignItems: 'center', gap: 8, marginBottom: 7 }}>
      {/* GoLive labels: LEFT-aligned (every caption starts on the same left edge —
          "Full Name:", "First Name:*", "Middle Name:" … line up as one straight
          column), trailing colon, required ones fully red (e.g. "First Name:*").
          They were right-aligned here, which left a ragged left edge that did not
          match GoLive. `nowrap` keeps a long caption from wrapping and breaking
          the column. Matches Client Details' `Field` and the mini popups' rows,
          which are already left-aligned. */}
      <label style={{ fontSize: 11, textAlign: 'left', whiteSpace: 'nowrap', color: req ? '#d33' : '#4a4f58' }}>{label}{label ? ':' : ''}{req ? '*' : ''}</label>
      {control}
    </div>
  );
  const ncText = (key: NcTextKey, req = false): ReactNode => (
    <input type="text" value={nc[key]} onChange={e => setNc(p => ({ ...p, [key]: e.target.value }))}
           style={ncInput(req && !nc[key].trim())} />
  );
  // GoLive geo row: dropdown + a New/Edit (✎) button that opens that entity's own
  // popup — GoLive puts one on EVERY geo lookup (Country, State AND City), each
  // opening the full editor incl. its nested child grid (State ▸ City, City ▸ Area)
  // — plus an eraser (✕) that clears the value back to N/A. onChange('') clears
  // (and, for Country/State, its own handler cascades the child fields).
  // `gated` = this lookup depends on a parent that isn't set yet, so the select
  // is greyed and lists nothing (GoLive Contact.cs `[DataSourceProperty(
  // "Country.States", SelectNothing)]` / `[DataSourceProperty("State.Cities",
  // SelectNothing)]` — State needs a Country, City needs a State).
  //
  // The New/Edit (✎) button follows the SAME dependency and is HIDDEN, not just
  // disabled, while gated — GoLive reveals it one level at a time: Country always
  // has it, State's appears once a Country is picked, City's once a State is
  // picked. (You cannot author a state without its country, or a city without its
  // state, so the editor simply isn't offered yet.) The eraser stays throughout so
  // a stale value can always be cleared.
  const ncGeoRow = (opts: GeoOpt[], value: string, onChange: (v: string) => void, parentVal?: string, onNew?: () => void, gated = false): ReactNode => (
    <div style={{ display: 'flex', gap: 4 }}>
      {/* GoLive button order: dropdown · NEW (+, opens a blank create form) · REMOVE (eraser). */}
      {ncGeo(opts, value, onChange, parentVal, gated)}
      {onNew && !gated && (
        <button type="button" className="ca-btn" style={{ padding: '2px 8px' }} title="New"
                onClick={onNew}><i className="ti ti-plus" /></button>
      )}
      <button type="button" className="ca-btn" style={{ padding: '2px 8px' }} title="Remove (N/A)"
              onClick={() => onChange('')} disabled={!value}><i className="ti ti-eraser" /></button>
    </div>
  );
  const ncGeo = (opts: GeoOpt[], value: string, onChange: (v: string) => void, parentVal?: string, gated = false): ReactNode => (
    <select value={value} onChange={e => onChange(e.target.value)} disabled={gated}
            title={gated ? 'Select the parent value first' : undefined}
            style={{ ...ncInput(false), background: gated ? '#f3f4f6' : '#fff',
                     color: gated ? '#9aa1ad' : 'inherit', cursor: gated ? 'not-allowed' : 'pointer' }}>
      <option value="">N/A</option>
      {/* A gated child lists nothing; otherwise only the parent's children. */}
      {(gated ? [] : opts.filter(o => parentVal === undefined || o.parentId === parentVal))
        .map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
    </select>
  );

  const filteredEmails = useMemo(() => {
    const t = pickerSearch.trim().toLowerCase();
    return t ? contactEmails.filter(c =>
      c.email.toLowerCase().includes(t) || c.name.toLowerCase().includes(t)) : contactEmails;
  }, [contactEmails, pickerSearch]);

  // Paged slice for the Contact Email lookup grid (GoLive pages its lookup lists).
  const emailTotalPages = Math.max(1, Math.ceil(filteredEmails.length / pickerPageSize));
  const pagedEmails = useMemo(
    () => filteredEmails.slice(pickerPage * pickerPageSize, (pickerPage + 1) * pickerPageSize),
    [filteredEmails, pickerPage, pickerPageSize]);
  // Keep the current page in range when the filter/page-size shrinks the list.
  useEffect(() => {
    if (pickerPage > emailTotalPages - 1) setPickerPage(emailTotalPages - 1);
  }, [pickerPage, emailTotalPages]);

  // ── Derived data ───────────────────────────────────────────────────────────
  // Status chips + global search filter here; AppDataGrid does per-column
  // filter, sort and pagination.
  const filtered = useMemo(() => {
    let r = rows;
    if (statusFilt !== 'All') r = r.filter(x => x.status === statusFilt);
    if (search.trim()) {
      const t = search.toLowerCase();
      r = r.filter(x =>
        x.jobId.toLowerCase().includes(t) ||
        x.projectId.toLowerCase().includes(t) ||
        x.projectName.toLowerCase().includes(t) ||
        x.client.toLowerCase().includes(t) ||
        x.email.toLowerCase().includes(t) ||
        x.sendBy.toLowerCase().includes(t) ||
        x.status.toLowerCase().includes(t)
      );
    }
    return r;
  }, [rows, statusFilt, search]);

  const sentCount    = useMemo(() => rows.filter(x => x.status === 'Sent').length,    [rows]);
  const inQueueCount = useMemo(() => rows.filter(x => x.status === 'InQueue').length, [rows]);
  const failedCount  = useMemo(() => rows.filter(x => x.status === 'Failed').length,  [rows]);
  const sentPct      = rows.length ? Math.round(sentCount / rows.length * 100) : 0;

  // Average TAT in days (one decimal)
  const avgTat = useMemo(() => {
    const v = rows.map(x => x.tatDays).filter((n): n is number => n !== null);
    if (v.length === 0) return '—';
    const avg = v.reduce((a, b) => a + b, 0) / v.length;
    return `${avg.toFixed(1)}d`;
  }, [rows]);

  // Shared-grid columns.  Date / TAT columns format via text; Method (blue pill)
  // and Status (hex colour) keep their original custom cell styling via render.
  const cols: AppColumn<SampleReceiptRow>[] = [
    { field: 'jobId',       header: 'Job ID',       width: 100 },
    { field: 'projectId',   header: 'Project ID',   width: 110 },
    { field: 'projectName', header: 'Project Name', width: 170 },
    { field: 'client',      header: 'Client',       width: 200 },
    { field: 'receivedDate', header: 'Received Date', width: 140,
      render: r => fmtDateTime(r.receivedDate), text: r => fmtDateTime(r.receivedDate) },
    { field: 'tatDays',     header: 'TAT',          width: 80,
      render: r => fmtTat(r.tatDays), text: r => fmtTat(r.tatDays) },
    { field: 'dueDate',     header: 'Due Date',     width: 100,
      render: r => fmtDate(r.dueDate), text: r => fmtDate(r.dueDate) },
    { field: 'email',       header: 'Email',        width: 240,
      text: r => r.email,
      render: r => (
        <span title={perm.write ? 'Double-click to change the recipient email' : undefined}
              style={{ cursor: perm.write ? 'pointer' : 'default' }}
              onDoubleClick={perm.write ? () => void openEmailPicker(r) : undefined}>
          {r.email || '—'}
        </span>) },
    { field: 'sendBy',      header: 'Send By',      width: 100 },
    // GoLive column parity: SendingMethod · MailStatus · SendDate.
    { field: 'method',      header: 'SendingMethod', width: 110,
      text: r => r.method,
      render: r => <span className="bl-pill info">{r.method}</span> },
    { field: 'status',      header: 'MailStatus',    width: 100,
      text: r => r.status,
      render: r => <span className={`bl-pill ${statusPillVariant(r.status)}`}>{r.status}</span> },
    { field: 'sentDate',    header: 'SendDate',      width: 110,
      render: r => fmtDate(r.sentDate), text: r => fmtDate(r.sentDate) },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="bl-page">

        {/* ── Fixed top: breadcrumb + title + KPIs ──────────────────────── */}
        <div className="bl-top">
          <div className="dp-page-hdr">
            <div className="dp-breadcrumb">
              <span>Home</span>
              <i className="ti ti-chevron-right" />
              <span>Sample Tracking</span>
              <i className="ti ti-chevron-right" />
              <span className="dp-bc-active">Sample Receipt Notification</span>
            </div>
            {/* Shared FormTitle — the ONE page heading (§18.6): nav icon/colour
                via navMeta + the TOP load-time chip. */}
            <div className="dp-title-row">
              {/* Audit Trail — VIEW-ONLY chip on the title row (BO UI Standard v2 /
                  standing rule 11). FormName "Sample Receipt Notification" matches
                  the audit rows written by Send + the Contact-Email change. */}
              <FormTitle pageId="sampreceipt" auditFormName="Sample Receipt Notification"
                         loadMs={loadMs} loading={loading} />
            </div>
            <div className="dp-subtitle">
              Read-only · {loading ? '…' : rows.length} items · {dateRange}
            </div>
          </div>

          {/* KPI row */}
          <div className="kpi-row">
            <div className="kpi">
              <div className="kpi-lbl">Total ({dateRange})</div>
              <div className="kpi-val kpi-blue">{loading ? '…' : rows.length}</div>
              <div className="kpi-chg">Items in range</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Sent</div>
              <div className="kpi-val kpi-teal">{loading ? '…' : sentCount}</div>
              <div className="kpi-chg" style={{ color: 'var(--ai-green)' }}>{sentPct}%</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Avg TAT</div>
              <div className="kpi-val">{loading ? '…' : avgTat}</div>
              <div className="kpi-chg">To due date</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Method</div>
              <div className="kpi-val" style={{ fontSize: 18 }}>Email</div>
              <div className="kpi-chg">All notifications</div>
            </div>
          </div>
        </div>

        {/* ── Grid area: toolbar + search sit ON the grey page (BO UI Standard
            v2); only the grid card below is white. ─────────────────────── */}
        <div className="bl-grid-area">
          {/* Toolbar on its OWN full-width row — canonical order: [left] ·
              Refresh · Export to.  This read grid has no New/Edit/Delete; the
              date filter rides in the left slot (shared .sa-date-* chip). */}
          <div style={{ margin: '0 0 8px' }}>
            {/* Toolbar order (user request): Date Range · Refresh · Reports · Export to.
                Date Range → left slot; Reports → beforeExport slot (between Refresh
                and Export); Send lives at the header top-right (above). */}
            <AppToolbar
              left={
                <DateRangeSelect variant="dropdown" label="Date Range" ranges={TRACKING_RANGES}
                  value={TRACKING_RANGES.find(r => r.label === dateRange)?.days ?? null}
                  onChange={(d) => setDateRange((TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All') as DateRangeKey)} />
              }
              onRefresh={load}
              refreshing={loading}
              beforeExport={
                /* Order: Reports ▾ · Send (both between Refresh and Export). */
                <>
                  {/* Reports ▾ → Sample Receipt (GoLive Reports dropdown). Reuses the
                     shared AppToolbar dropdown classes for one visual style. */}
                  <span className="app-toolbar__export">
                    <button className="ca-btn" onClick={() => setReportsOpen(o => !o)}
                            disabled={selected.size === 0} title="Reports">
                      <span className="tb-i tb-i-blue"><i className="ti ti-file-text" /></span> Reports ▾
                    </button>
                    {reportsOpen && (
                      <>
                        <div className="app-toolbar__export-backdrop" onClick={() => setReportsOpen(false)} />
                        <div className="app-toolbar__export-menu" role="menu">
                          {(reportItems.length
                            ? reportItems
                            : [{ id: -1, label: 'Sample Receipt', templateName: 'xrReceiptNotification', allowMultipleJobId: true }]
                          ).map(it => (
                            <button key={it.id} className="app-toolbar__export-item" role="menuitem"
                                    onClick={() => runReport(it)}>
                              <i className="ti ti-file-text" /> {it.label}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </span>
                  {/* Send (GoLive Send → Sendmail) — beside Reports, before Export.
                     Write-gated (MAC): hidden when the user's MAC level has no Write. */}
                  {perm.write && (
                    <button className="ca-btn" onClick={sendSelected}
                            disabled={selected.size === 0 || sending} title="Send">
                      {sending ? <BusyIcon busy icon="ti-send" /> : <span className="tb-i tb-i-teal"><i className="ti ti-mail" /></span>} Send
                    </button>
                  )}
                </>
              }
              exportData={{ filename: 'sample-receipt-notification', ...gridExportMatrix(gridRows ?? filtered, cols) }}
            />
          </div>

          {/* Global search — shared .list-search-row on the grey page, never inside the card. */}
          <div className="list-search-row">
            <div className="list-search">
              <span className="tb-i"><i className="ti ti-search" /></span>
              <input
                ref={searchRef}
                type="text"
                placeholder="Search job ID, project, client, email, send by…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <span className="list-search-clear" onClick={() => setSearch('')}><i className="ti ti-x" /></span>
              )}
            </div>
            {(filtered.length !== rows.length) && (
              <span className="search-match-count">
                {filtered.length} match{filtered.length !== 1 ? 'es' : ''}
              </span>
            )}
          </div>

          {/* ONE white grid card — filter-chip strip + grid + its single footer. */}
          <div className="bl-card bl-card-flex">

            {/* Status filter — shared .sa-filter-* chips (backlog.css) */}
            <div className="sa-filter-row">
              <span className="sa-filter-lbl">Status:</span>
              {([
                { id: 'All',     count: rows.length },
                { id: 'Sent',    count: sentCount },
                { id: 'InQueue', count: inQueueCount },
                { id: 'Failed',  count: failedCount },
              ] as { id: StatusFilter; count: number }[]).map(t => (
                <button
                  key={t.id}
                  className={`sa-filter-btn${statusFilt === t.id ? ' active' : ''}`}
                  onClick={() => setStatusFilt(t.id)}
                >
                  {t.id} ({t.count})
                </button>
              ))}
            </div>

            {/* Table — load failure stays inline; AppDataGrid handles the rest */}
            {!perm.read ? (
              /* Read-gate (MAC, GoLive parity): Navigate without Read shows the
                 form chrome but no data. */
              <div className="bl-err" style={{ margin: 12 }}>
                <i className="ti ti-lock" /> You don&apos;t have Read permission for this form (Module Access Control).
              </div>
            ) : error ? (
              <div className="bl-err" style={{ margin: 12 }}>
                <i className="ti ti-alert-circle" /> {error}
                <button onClick={load} style={{ marginLeft: 10, color: 'var(--brand-accent, #e07b39)', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Retry</button>
              </div>
            ) : (
              /* Shared data grid — styling, per-column filters, sort, footer /
                 pagination and load-time are all standardised in <AppDataGrid>. */
              <AppDataGrid<SampleReceiptRow>
                rows={filtered}
                columns={cols}
                getRowId={r => r.jobId}
                loading={loading}
                loadMs={loadMs}
                emptyText="No records found"
                selectable
                selectedIds={selected}
                onSelectionChange={setSelected}
                onVisibleRowsChange={setGridRows}
              />
            )}
          </div>
        </div>
      </div>

      {/* Contact Email popup — double-click the Email cell. GoLive-exact: plain
          "Contact Email" title (no job-id suffix), top-right search box + orange
          search button, checkbox list under a grid-style "Email" header, a pager
          footer, and solid-orange OK/Cancel/New (GoLive Contact_LookupListView). */}
      {pickerRow && (
        <div onClick={() => setPickerRow(null)}
             style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000,
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
               style={{ background: '#fff', borderRadius: 8,
                        width: pickerMax ? '96vw' : 560, maxWidth: '96vw',
                        height: pickerMax ? '94vh' : undefined, maxHeight: pickerMax ? '94vh' : '80vh',
                        display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
                        border: '2px solid var(--brand-accent, #e8670a)' }}>
            <div style={{ padding: '10px 14px', fontWeight: 600, borderBottom: '1px solid #e5e7eb',
                          color: 'var(--ai-ink, #1f2430)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Contact Email</span>
              {/* Maximize + Close — GoLive chrome on every popup. */}
              <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button type="button" title={pickerMax ? 'Restore' : 'Maximize'} onClick={() => setPickerMax(m => !m)}
                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, color: 'var(--brand-accent, #e8670a)', lineHeight: 1 }}><i className={`ti ${pickerMax ? 'ti-minimize' : 'ti-maximize'}`} /></button>
                <button type="button" title="Close" onClick={() => setPickerRow(null)}
                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18, color: 'var(--brand-accent, #e8670a)', lineHeight: 1 }}><i className="ti ti-x" /></button>
              </span>
            </div>
            {/* Search — top-right, fixed width, orange search button (GoLive parity). */}
            <div style={{ padding: '12px 12px 0', display: 'flex', justifyContent: 'flex-end' }}>
              <div style={{ display: 'flex', width: 220, border: '1px solid #cbd5e1', borderRadius: 6, overflow: 'hidden' }}>
                <input type="text" value={pickerSearch} onChange={e => { setPickerSearch(e.target.value); setPickerPage(0); }}
                       style={{ flex: 1, minWidth: 0, border: 'none', padding: '6px 8px', fontSize: 12.5, outline: 'none' }} />
                <button type="button" title="Search" tabIndex={-1}
                        style={{ border: 'none', borderLeft: '1px solid #cbd5e1', width: 30, cursor: 'default',
                                 background: 'var(--brand-accent, #e8670a)', color: '#fff', fontSize: 12 }}><i className="ti ti-search" /></button>
              </div>
            </div>
            {/* Grid-style list — header row + checkbox rows (GoLive Contact_LookupListView). */}
            <div style={{ margin: '10px 12px 0', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden',
                          ...(pickerMax ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : null) }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#fafbfc',
                            borderBottom: '1px solid #e5e7eb', fontSize: 11.5, fontWeight: 600, color: '#4a4f58' }}>
                {/* Header checkbox = select-all over the filtered list (GoLive). */}
                <input type="checkbox"
                       checked={filteredEmails.length > 0 && filteredEmails.every(c => pickerSel.includes(c.oid))}
                       ref={el => { if (el) el.indeterminate = pickerSel.length > 0 && !filteredEmails.every(c => pickerSel.includes(c.oid)); }}
                       onChange={e => setPickerSel(e.target.checked ? filteredEmails.map(c => c.oid) : [])} />
                <span style={{ flex: 1 }}>Email</span>
                <span style={{ color: '#9aa1ad' }}><i className="ti ti-chevron-down" /></span>
              </div>
              <div style={{ overflowY: 'auto', minHeight: 60, ...(pickerMax ? { flex: 1 } : { maxHeight: 220 }) }}>
                {pickerLoading ? (
                  <div style={{ padding: 16, color: '#6b7280' }}>Loading…</div>
                ) : filteredEmails.length === 0 ? (
                  <div style={{ padding: 16, color: '#6b7280' }}>
                    No contact emails for this client. Use <b>New</b> to enter one.
                  </div>
                ) : pagedEmails.map(c => (
                  <label key={c.oid}
                         style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                                  borderBottom: '1px solid #f1f3f7', cursor: 'pointer', fontSize: 12.5 }}>
                    <input type="checkbox" checked={pickerSel.includes(c.oid)}
                           onChange={() => setPickerSel(s => s.includes(c.oid) ? s.filter(x => x !== c.oid) : [...s, c.oid])} />
                    <span style={{ color: 'var(--ai-ink, #1f2430)' }}>{c.email}</span>
                  </label>
                ))}
              </div>
            </div>
            {/* Pager footer — GoLive lookup-grid parity: count · numbered pages ·
                page-size dropdown (the Client picker's footer, same shape). */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          margin: '8px 12px 0', fontSize: 11.5, color: '#6b7280' }}>
              <span>Page {pickerPage + 1} of {emailTotalPages} ({filteredEmails.length} item{filteredEmails.length === 1 ? '' : 's'})</span>
              <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <button type="button" disabled={pickerPage === 0} onClick={() => setPickerPage(p => Math.max(0, p - 1))}
                        style={{ border: '1px solid #e5e7eb', background: '#fff', borderRadius: 4, width: 22, height: 22,
                                 cursor: pickerPage === 0 ? 'not-allowed' : 'pointer', color: pickerPage === 0 ? '#c3c9d2' : '#4a4f58' }}><i className="ti ti-chevron-left" /></button>
                {Array.from({ length: emailTotalPages }, (_, i) => i).slice(0, 8).map(i => (
                  <button key={i} type="button" onClick={() => setPickerPage(i)}
                          style={{ padding: '1px 7px', borderRadius: 4, cursor: 'pointer', background: '#fff',
                                   border: `1px solid ${i === pickerPage ? 'var(--brand-accent, #e8670a)' : '#e5e7eb'}`,
                                   color: i === pickerPage ? 'var(--brand-accent, #e8670a)' : '#4a4f58' }}>{i + 1}</button>
                ))}
                <button type="button" disabled={pickerPage >= emailTotalPages - 1} onClick={() => setPickerPage(p => Math.min(emailTotalPages - 1, p + 1))}
                        style={{ border: '1px solid #e5e7eb', background: '#fff', borderRadius: 4, width: 22, height: 22,
                                 cursor: pickerPage >= emailTotalPages - 1 ? 'not-allowed' : 'pointer', color: pickerPage >= emailTotalPages - 1 ? '#c3c9d2' : '#4a4f58' }}><i className="ti ti-chevron-right" /></button>
              </span>
              <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                Page size:
                <select value={pickerPageSize} onChange={e => { setPickerPageSize(Number(e.target.value)); setPickerPage(0); }}
                        style={{ border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 11.5, padding: '1px 4px' }}>
                  {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 12, marginTop: 10,
                          borderTop: '1px solid #e5e7eb' }}>
              {/* Always enabled — GoLive parity: clicking with nothing ticked is
                  what raises "Select email id to continue". */}
              {/* OK: join the selected contacts' emails (dedup) with ", " to match
                  the grid's existing multi-recipient format — the Send path splits
                  Samplecheckin.Email on ';'/',' so either separator still works. */}
              <button className="ca-btn ca-btn-solid" onClick={() => {
                const emails = Array.from(new Set(
                  contactEmails.filter(c => pickerSel.includes(c.oid)).map(c => c.email.trim()).filter(Boolean)));
                void applyEmail(emails.join(', '));
              }}>OK</button>
              <button className="ca-btn ca-btn-solid" onClick={() => setPickerRow(null)}>Cancel</button>
              <button className="ca-btn ca-btn-solid" onClick={() => { setNc({ ...EMPTY_CONTACT }); setNcMax(false); setNcOpen(true); }}>
                <span style={{ color: '#fff' }}><i className="ti ti-plus" /></span> New
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Contact form (Contact Email popup → "New") — GoLive Contact dialog,
          AI-styled. Creates a client Contact then sets it as the job recipient. */}
      {ncOpen && (
        // Backdrop closes only on a DIRECT click — a bubbled close-X click from a
        // nested Country/State/City popup must not close this New Contact form.
        <div onClick={e => { if (e.target === e.currentTarget) setNcOpen(false); }}
             style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1100,
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
               style={{ background: '#fff', borderRadius: 8,
                        width: ncMax ? '96vw' : 720, maxWidth: '96vw',
                        height: ncMax ? '94vh' : undefined, maxHeight: '94vh',
                        display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.28)',
                        border: '2px solid var(--brand-accent, #e8670a)' }}>
            {/* Birthday: keep the calendar picker but hide the "mm/dd/yyyy" text while
                empty (GoLive parity — the field looks blank, the calendar icon stays). */}
            <style>{`
              input.srn-bday-empty::-webkit-datetime-edit { color: transparent; }
              input.srn-bday-empty:focus::-webkit-datetime-edit { color: inherit; }
            `}</style>
            {/* Title "Contact" + orange maximize/close chrome (GoLive parity). No client
                caption in the title bar. */}
            <div style={{ padding: '10px 14px', fontWeight: 600, borderBottom: '1px solid #e5e7eb',
                          color: 'var(--ai-ink, #1f2430)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Contact</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button type="button" title={ncMax ? 'Restore' : 'Maximize'} onClick={() => setNcMax(m => !m)}
                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, color: 'var(--brand-accent, #e8670a)', lineHeight: 1 }}><i className={`ti ${ncMax ? 'ti-minimize' : 'ti-maximize'}`} /></button>
                <button type="button" title="Close" onClick={() => setNcOpen(false)}
                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18, color: 'var(--brand-accent, #e8670a)', lineHeight: 1 }}><i className="ti ti-x" /></button>
              </span>
            </div>
            <div style={{ padding: 16, overflowY: 'auto' }}>
              {/* Two columns, GoLive Contact-dialog field order/positions. */}
              <div style={{ display: 'flex', gap: 22 }}>
                {/* LEFT column */}
                <div style={{ flex: 1 }}>
                  {ncRow('Full Name', <input type="text" readOnly value={`${nc.firstName} ${nc.middleName} ${nc.lastName}`.replace(/\s+/g, ' ').trim()} style={{ ...ncInput(false), background: '#f3f4f6', color: '#4a4f58' }} />)}
                  {ncRow('First Name', ncText('firstName', true), true)}
                  {ncRow('Middle Name', ncText('middleName'))}
                  {ncRow('Last Name', ncText('lastName'))}
                  {ncRow('Gender', (
                    <select value={nc.gender} onChange={e => setNc(p => ({ ...p, gender: e.target.value }))} style={ncInput(false)}>
                      <option value="">None</option><option value="1">Male</option><option value="2">Female</option>
                    </select>
                  ))}
                  {/* Birthday: calendar picker; the mm/dd/yyyy text stays hidden while
                      empty (srn-bday-empty), the calendar icon remains — GoLive parity. */}
                  {ncRow('Birthday', <input type="date" className={nc.birthday ? 'srn-bday' : 'srn-bday srn-bday-empty'}
                                            value={nc.birthday} onChange={e => setNc(p => ({ ...p, birthday: e.target.value }))} style={ncInput(false)} />)}
                  {/* Client — HIDDEN while "Prospect" is ticked. GoLive declares this
                      on the BO itself (Contact.cs lines 41-44):
                        [Appearance("hideCustomer", TargetItems="Customer;",
                                    Criteria="[Prospect] = False", Visibility=Show)]
                        [Appearance("showCustomer", TargetItems="Customer;",
                                    Criteria="[Prospect] = True",  Visibility=Hide)]
                      (the legacy rule NAMES are swapped relative to their effect —
                      the criteria/visibility pairs are what matter). The mirror rules
                      on `CRMProspect` show a CRMProspects lookup — itself captioned
                      "Client" via [XafDisplayName("Client")], sourced from
                      ProsClientDS = CRMProspects WHERE [Status] = 'None' — but that
                      item is not laid out in the Contact view this popup mirrors, so
                      GoLive shows NO replacement row here: ticking Prospect simply
                      drops the Client row and the Prospect checkbox moves up. Same
                      here. The picked clientId is deliberately NOT cleared on tick —
                      XAF keeps the Customer value too, and it reappears untouched if
                      Prospect is unticked.
                      Client: N/A + the lookup button opening the Client picker AND the
                      eraser that clears it back to N/A — GoLive puts BOTH buttons on
                      this lookup, exactly like the Country/State/City rows. */}
                  {!nc.prospect && ncRow('Client', (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <input type="text" readOnly value={nc.clientName || 'N/A'}
                             style={{ ...ncInput(false), background: '#f3f4f6', color: '#4a4f58' }} />
                      <button type="button" className="ca-btn" style={{ padding: '2px 8px' }} title="Choose Client"
                              onClick={() => {
                                // Re-sync the tick to the value actually held by the
                                // form: a stale clientSel from a previous visit left a
                                // row looking checked after the picker was submitted.
                                setClientSel(nc.clientId);
                                setClientSearch(''); setColFilter({ name: [], address: [], phone: [] });
                                setOpenColFilter(null); setClientPage(0); setClientPickerMax(false); setClientPickerOpen(true);
                              }}><i className="ti ti-pencil" /></button>
                      <button type="button" className="ca-btn" style={{ padding: '2px 8px' }} title="Remove (N/A)"
                              disabled={!nc.clientId}
                              onClick={() => setNc(p => ({ ...p, clientId: '', clientName: '' }))}><i className="ti ti-eraser" /></button>
                    </div>
                  ), true)}
                  {ncRow('', (
                    <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, cursor: 'pointer' }}>
                      <input type="checkbox" checked={nc.prospect} onChange={e => setNc(p => ({ ...p, prospect: e.target.checked }))} /> Prospect
                    </label>
                  ))}
                  {ncRow('Position', ncText('position'))}
                  {ncRow('Account', ncText('account'))}
                  {ncRow('Email', ncText('email', true), true)}
                  {ncRow('Office Phone', ncText('officePhone'))}
                  {ncRow('Home Phone', ncText('homePhone'))}
                </div>
                {/* RIGHT column */}
                <div style={{ flex: 1 }}>
                  {ncRow('Mobile Phone', ncText('mobilePhone'))}
                  {ncRow('Other Phone', ncText('otherPhone'))}
                  {ncRow('Fax', ncText('fax'))}
                  {ncRow('Street 1', ncText('street1'))}
                  {ncRow('Street 2', ncText('street2'))}
                  {ncRow('Country', ncGeoRow(geoCountries, nc.countryId, v => setNc(p => ({ ...p, countryId: v, stateId: '', cityId: '' })), undefined, () => setCountryModalOpen('new')))}
                  {ncRow('State', ncGeoRow(geoStates, nc.stateId, v => setNc(p => ({ ...p, stateId: v, cityId: '' })), nc.countryId, () => setStateModalOpen('new'), !nc.countryId))}
                  {ncRow('City', ncGeoRow(geoCities, nc.cityId, v => setNc(p => ({ ...p, cityId: v })), nc.stateId, () => setCityModalOpen('new'), !nc.countryId || !nc.stateId))}
                  {ncRow('Zip', ncText('zip'))}
                  {/* Photo — GoLive XAF ImageEditor: EMPTY shows the readonly box +
                      orange Browse… button; once an image is picked it is REPLACED by
                      the image PREVIEW with the small edit (pencil) / erase buttons
                      underneath — not a filename in a text box. Reuses the shared
                      .cty-flag-lg / .cty-flag-acts / .cty-flag-btn styles already
                      built for the Country National Flag editor (standing rule 8),
                      so both image fields look and behave identically.
                      photoBase64 holds the full data URL, which is both the <img>
                      src here and what the API stores — ParsePhoto strips the
                      "data:image/…;base64," prefix server-side. */}
                  {ncRow('Photo', (
                    <>
                      <input ref={ncPhotoRef} type="file" accept="image/*" style={{ display: 'none' }}
                             onChange={e => {
                               const f = e.target.files?.[0];
                               e.target.value = '';        // re-picking the SAME file must still fire
                               if (!f) return;
                               const r = new FileReader();
                               r.onload = () => setNc(p => ({ ...p, photoBase64: String(r.result), photoName: f.name }));
                               r.readAsDataURL(f);
                             }} />
                      {nc.photoBase64 ? (
                        <div>
                          <img className="cty-flag-lg" src={nc.photoBase64} alt={nc.photoName || 'Contact photo'} />
                          <div className="cty-flag-acts">
                            <button type="button" className="cty-flag-btn" title="Edit / choose image"
                                    onClick={() => ncPhotoRef.current?.click()}><i className="ti ti-pencil" /></button>
                            <button type="button" className="cty-flag-btn" title="Erase"
                                    onClick={() => setNc(p => ({ ...p, photoBase64: '', photoName: '' }))}><i className="ti ti-eraser" /></button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <input type="text" readOnly value="" onClick={() => ncPhotoRef.current?.click()}
                                 style={{ ...ncInput(false), background: '#f3f4f6', color: '#4a4f58', cursor: 'pointer' }} />
                          <button type="button" className="ca-btn ca-btn-solid" style={{ whiteSpace: 'nowrap' }}
                                  onClick={() => ncPhotoRef.current?.click()}>Browse…</button>
                        </div>
                      )}
                    </>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 24, marginTop: 10, paddingTop: 10, borderTop: '1px dashed #e5e7eb' }}>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={nc.sampleReceiptNotification}
                         onChange={e => setNc(p => ({ ...p, sampleReceiptNotification: e.target.checked }))} />
                  Sample Receipt Notification
                </label>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={nc.reportDelivery}
                         onChange={e => setNc(p => ({ ...p, reportDelivery: e.target.checked }))} />
                  Report Delivery
                </label>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 12, borderTop: '1px solid #e5e7eb' }}>
              {/* OK/Cancel — solid orange, white text (GoLive parity); OK always
                  enabled, saveNewContact validates on click. */}
              <button type="button" onClick={() => void saveNewContact()} disabled={ncSaving} style={ncActionBtn}>OK</button>
              <button type="button" onClick={() => setNcOpen(false)} style={ncActionBtn}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Client picker (GoLive Client lookup popup) — GoLive-exact grid: checkbox +
          separate Edit (pencil) icon column, sortable Client/Address/Phone headers,
          top-right search + orange search button, pager footer, solid-orange OK/Cancel. */}
      {clientPickerOpen && (
        <div onClick={() => setClientPickerOpen(false)}
             style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1200,
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
               style={{ background: '#fff', borderRadius: 8,
                        width: clientPickerMax ? '96vw' : 900, maxWidth: '96vw',
                        height: clientPickerMax ? '94vh' : undefined, maxHeight: clientPickerMax ? '94vh' : '86vh',
                        display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.28)',
                        border: '2px solid var(--brand-accent, #e8670a)' }}>
            <div style={{ padding: '10px 14px', fontWeight: 600, borderBottom: '1px solid #e5e7eb',
                          color: 'var(--ai-ink, #1f2430)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Client</span>
              {/* Maximize + Close — GoLive chrome on every popup. */}
              <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button type="button" title={clientPickerMax ? 'Restore' : 'Maximize'} onClick={() => setClientPickerMax(m => !m)}
                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, color: 'var(--brand-accent, #e8670a)', lineHeight: 1 }}><i className={`ti ${clientPickerMax ? 'ti-minimize' : 'ti-maximize'}`} /></button>
                <button type="button" title="Close" onClick={() => setClientPickerOpen(false)}
                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18, color: 'var(--brand-accent, #e8670a)', lineHeight: 1 }}><i className="ti ti-x" /></button>
              </span>
            </div>
            {/* Search — top-right, orange search button (GoLive parity). */}
            <div style={{ padding: '12px 12px 0', display: 'flex', justifyContent: 'flex-end' }}>
              <div style={{ display: 'flex', width: 240, border: '1px solid #cbd5e1', borderRadius: 6, overflow: 'hidden' }}>
                <input type="text" value={clientSearch} onChange={e => { setClientSearch(e.target.value); setClientPage(0); }}
                       style={{ flex: 1, minWidth: 0, border: 'none', padding: '6px 8px', fontSize: 12.5, outline: 'none' }} />
                <button type="button" title="Search" tabIndex={-1}
                        style={{ border: 'none', borderLeft: '1px solid #cbd5e1', width: 30, cursor: 'default',
                                 background: 'var(--brand-accent, #e8670a)', color: '#fff', fontSize: 12 }}><i className="ti ti-search" /></button>
              </div>
            </div>
            <div style={{ flex: 1, overflow: 'auto', margin: '10px 12px 0', border: '1px solid #e5e7eb', borderRadius: 6 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#fafbfc', position: 'sticky', top: 0 }}>
                    <th style={{ width: 28, padding: '6px 4px', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                      <input type="checkbox" checked={false} readOnly style={{ visibility: 'hidden' }} />
                    </th>
                    <th style={{ width: 26, padding: '6px 4px', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }} />
                    {(['name', 'address', 'phone'] as const).map((key, i) => (
                      <th key={key}
                          style={{ position: 'relative', textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e5e7eb',
                                   borderRight: i < 2 ? '1px solid #e5e7eb' : undefined, userSelect: 'none',
                                   color: '#4a4f58', fontWeight: 600 }}>
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                          <span style={{ cursor: 'pointer' }}
                                onClick={() => setClientSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' })}>
                            {key === 'name' ? 'Client' : key === 'address' ? 'Address' : 'Phone'}{' '}
                            <span style={{ color: clientSort.key === key ? 'var(--brand-accent, #e8670a)' : '#9aa1ad' }}>
                              {clientSort.key === key ? <i className={`ti ${clientSort.dir === 'asc' ? 'ti-chevron-up' : 'ti-chevron-down'}`} /> : null}
                            </span>
                          </span>
                          {/* Column filter dropdown (GoLive header chevron) — orange when active. */}
                          <button type="button" title="Filter"
                                  onClick={() => { setOpenColFilter(o => o === key ? null : key); setColFilterSearch(''); }}
                                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', lineHeight: 1,
                                           color: colFilter[key].length ? 'var(--brand-accent, #e8670a)' : '#9aa1ad', fontSize: 12 }}><i className="ti ti-filter" /></button>
                        </span>
                        {openColFilter === key && (
                          <div onClick={e => e.stopPropagation()}
                               style={{ position: 'absolute', top: '100%', left: i === 2 ? undefined : 0, right: i === 2 ? 0 : undefined,
                                        zIndex: 30, marginTop: 2, width: 240, background: '#fff', border: '1px solid #cbd5e1',
                                        borderRadius: 6, boxShadow: '0 6px 20px rgba(0,0,0,0.18)', padding: 8, fontWeight: 400 }}>
                            <input type="text" autoFocus value={colFilterSearch} onChange={e => setColFilterSearch(e.target.value)}
                                   placeholder="Enter text to filter…"
                                   style={{ width: '100%', boxSizing: 'border-box', padding: '4px 7px', border: '1px solid #cbd5e1', borderRadius: 5, fontSize: 12, marginBottom: 6 }} />
                            <label style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '3px 4px', fontSize: 12, cursor: 'pointer' }}>
                              <input type="checkbox" checked={colFilter[key].length === 0}
                                     onChange={() => { setColFilter(f => ({ ...f, [key]: [] })); setClientPage(0); }} />
                              (All)
                            </label>
                            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                              {colFilterValues.map(v => (
                                <label key={v || '(blank)'} style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '3px 4px', fontSize: 12, cursor: 'pointer' }}>
                                  <input type="checkbox" checked={colFilter[key].includes(v)}
                                         onChange={() => {
                                           setColFilter(f => {
                                             const has = f[key].includes(v);
                                             return { ...f, [key]: has ? f[key].filter(x => x !== v) : [...f[key], v] };
                                           });
                                           setClientPage(0);
                                         }} />
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v || '(Blanks)'}</span>
                                </label>
                              ))}
                              {colFilterValues.length === 0 && (
                                <div style={{ padding: '6px 4px', color: '#9aa1ad', fontSize: 12 }}>No values.</div>
                              )}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 6, borderTop: '1px solid #eef0f3', paddingTop: 6 }}>
                              <button type="button" className="ca-btn" style={{ padding: '2px 10px' }}
                                      onClick={() => { setColFilter(f => ({ ...f, [key]: [] })); setClientPage(0); }}>Clear</button>
                              <button type="button" className="ca-btn ca-btn-solid" style={{ padding: '2px 10px' }}
                                      onClick={() => setOpenColFilter(null)}>Close</button>
                            </div>
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedClients.map(c => (
                    <tr key={c.oid} style={{ cursor: 'pointer', background: clientSel === c.oid ? '#eef1f6' : undefined }}
                        onClick={() => setClientSel(c.oid)}
                        onMouseEnter={e => { if (clientSel !== c.oid) e.currentTarget.style.background = '#f7f8fa'; }}
                        onMouseLeave={e => { if (clientSel !== c.oid) e.currentTarget.style.background = ''; }}>
                      <td style={{ padding: '5px 4px', borderBottom: '1px solid #f0f0f0', borderRight: '1px solid #f0f0f0', textAlign: 'center' }}>
                        <input type="checkbox" checked={clientSel === c.oid}
                               onChange={() => setClientSel(clientSel === c.oid ? '' : c.oid)}
                               onClick={e => e.stopPropagation()} />
                      </td>
                      <td style={{ padding: '5px 4px', borderBottom: '1px solid #f0f0f0', borderRight: '1px solid #f0f0f0', textAlign: 'center' }}>
                        <button type="button" title="Edit" className="tb-i tb-i-blue"
                                style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13 }}
                                onClick={e => { e.stopPropagation(); void openClientDetail(c.oid); }}><i className="ti ti-pencil" /></button>
                      </td>
                      <td style={{ padding: '5px 8px', borderBottom: '1px solid #f0f0f0' }}>{c.name}</td>
                      <td style={{ padding: '5px 8px', borderBottom: '1px solid #f0f0f0', color: '#4a4f58' }}>{c.address}</td>
                      <td style={{ padding: '5px 8px', borderBottom: '1px solid #f0f0f0', color: '#4a4f58' }}>{c.phone}</td>
                    </tr>
                  ))}
                  {clients.length === 0 && (
                    <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}>Loading clients…</td></tr>
                  )}
                  {clients.length > 0 && sortedFilteredClients.length === 0 && (
                    <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}>No clients match your search.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Pager footer (GoLive lookup-grid parity). */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          margin: '8px 12px 0', fontSize: 11.5, color: '#6b7280' }}>
              <span>Page {clientTotalPages === 0 ? 0 : clientPage + 1} of {clientTotalPages} ({sortedFilteredClients.length} items)</span>
              <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <button type="button" disabled={clientPage === 0} onClick={() => setClientPage(p => Math.max(0, p - 1))}
                        style={{ border: '1px solid #e5e7eb', background: '#fff', borderRadius: 4, width: 22, height: 22,
                                 cursor: clientPage === 0 ? 'not-allowed' : 'pointer', color: clientPage === 0 ? '#c3c9d2' : '#4a4f58' }}><i className="ti ti-chevron-left" /></button>
                {Array.from({ length: clientTotalPages }, (_, i) => i).slice(0, 8).map(i => (
                  <button key={i} type="button" onClick={() => setClientPage(i)}
                          style={{ padding: '1px 7px', borderRadius: 4, cursor: 'pointer',
                                   border: `1px solid ${i === clientPage ? 'var(--brand-accent, #e8670a)' : '#e5e7eb'}`,
                                   background: i === clientPage ? '#fff' : '#fff',
                                   color: i === clientPage ? 'var(--brand-accent, #e8670a)' : '#4a4f58' }}>{i + 1}</button>
                ))}
                <button type="button" disabled={clientPage >= clientTotalPages - 1} onClick={() => setClientPage(p => Math.min(clientTotalPages - 1, p + 1))}
                        style={{ border: '1px solid #e5e7eb', background: '#fff', borderRadius: 4, width: 22, height: 22,
                                 cursor: clientPage >= clientTotalPages - 1 ? 'not-allowed' : 'pointer', color: clientPage >= clientTotalPages - 1 ? '#c3c9d2' : '#4a4f58' }}><i className="ti ti-chevron-right" /></button>
              </span>
              <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                Page size:
                <select value={clientPageSize} onChange={e => { setClientPageSize(Number(e.target.value)); setClientPage(0); }}
                        style={{ border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 11.5, padding: '1px 4px' }}>
                  {[20, 50, 100, 200, 500, 1000, 2000].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 12, marginTop: 10, borderTop: '1px solid #e5e7eb' }}>
              <button type="button" className="ca-btn ca-btn-solid" disabled={!clientSel}
                      onClick={() => { const c = clients.find(x => x.oid === clientSel); if (c) setNc(p => ({ ...p, clientId: c.oid, clientName: c.name })); setClientPickerOpen(false); }}>OK</button>
              <button type="button" className="ca-btn ca-btn-solid" onClick={() => setClientPickerOpen(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Client Details (Edit icon in the Client picker) — GoLive "{Client} - Clients"
          full detail: Summary / Address / Account Management / Sending Preference +
          Contacts / Invoicing Contact / Projects / Collectors / Sample Sites / Notes
          tabs, each with its own New/Edit/Delete/Export grid. */}
      {clientDetailOpen && (
        <ClientDetailModal
          clientId={clientDetailId}
          onClose={() => setClientDetailOpen(false)}
          onSaved={() => { setClientDetailOpen(false); loadClients(); }}
          notify={notify}
        />
      )}

      {/* Country create/edit popup (GoLive Country edit icon in the New Contact form).
          Reuses /api/v1/system/countries + /states. On save, refresh the geo lists
          and select the saved country. */}
      {countryModalOpen && (
        <CountryDetailModal
          countryId={countryModalOpen === 'edit' ? (nc.countryId || null) : null}
          onClose={() => setCountryModalOpen(false)}
          onSaved={async (savedId) => {
            setCountryModalOpen(false);
            await loadGeo();
            setNc(p => ({ ...p, countryId: savedId, stateId: '', cityId: '' }));
          }}
          notify={notify}
        />
      )}

      {/* State create/edit popup (GoLive State ✎ icon) — State* / Short Name /
          Country + the nested City grid. On save, refresh the geo lists and
          select the saved state. */}
      {stateModalOpen && (
        <StateDetailModal
          stateId={stateModalOpen === 'edit' ? (nc.stateId || null) : null}
          countryId={nc.countryId || null}
          onClose={() => setStateModalOpen(false)}
          onSaved={async (savedId) => {
            setStateModalOpen(false);
            await loadGeo();
            setNc(p => ({ ...p, stateId: savedId, cityId: '' }));
          }}
          notify={notify}
        />
      )}

      {/* City create/edit popup (GoLive City ✎ icon) — City* / State + the nested
          Area grid. On save, refresh the geo lists and select the saved city. */}
      {cityModalOpen && (
        <CityDetailModal
          cityId={cityModalOpen === 'edit' ? (nc.cityId || null) : null}
          stateId={nc.stateId || null}
          onClose={() => setCityModalOpen(false)}
          onSaved={async (savedId) => {
            setCityModalOpen(false);
            await loadGeo();
            setNc(p => ({ ...p, cityId: savedId }));
          }}
          notify={notify}
        />
      )}
    </>
  );
}

// OK / Cancel button — solid orange background, white text (GoLive parity).
const ncActionBtn: CSSProperties = {
  background: 'var(--brand-accent, #e8670a)', border: '1px solid var(--brand-accent, #e8670a)', color: '#fff',
  borderRadius: 4, padding: '5px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};
